import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form, Typography } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import UserSelector from '../../components/UserSelector';

const statusMap = {
    0: { text: '未知', type: 'default' },
    1: { text: '通过', type: 'success' },
    2: { text: '失败', type: 'danger' },
};

const UserVerify = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchTyp, setSearchTyp] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [typList, setTypList] = useState([]);
    const [showResultModal, setShowResultModal] = useState(false);
    const [resultModalContent, setResultModalContent] = useState('');

    const fetchTypList = async () => {
        try {
            const res = await request.get('/go/sp/su/list_user_verify_typ/');
            if (res.errno === 0) {
                setTypList(res.list || []);
            }
        } catch { }
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/su/list_user_verify/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    typ: searchTyp === null ? undefined : searchTyp
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTypList();
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/su/add_user_verify/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/su/edit_user_verify/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/su/delete_user_verify/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的记录');
            return;
        }
        try {
            const response = await request.post('/go/sp/su/batchedit_user_verify/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '用户',
            dataIndex: 'su_uid',
            width: 180,
            render: (uid, record) => (
                <div>
                    <div>{record.su?.name || record.su_uid}</div>
                </div>
            )
        },
        {
            title: '信用类型',
            dataIndex: 'typ',
            width: 150,
            render: (typ) => {
                const item = typList.find(i => i.uid == typ);
                return item ? item.name : typ;
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '参数',
            dataIndex: 'param',
            width: 180,
            render: (text) => <Typography.Paragraph ellipsis={{ rows: 2 }}>{text}</Typography.Paragraph>
        },
        {
            title: '结果',
            dataIndex: 'result',
            width: 300,
            render: (text) => {
                const maxLen = 200;
                const isLong = text && text.length > maxLen;
                const displayText = isLong ? text.slice(0, maxLen) + '...' : text;
                return (
                    <div>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, display: 'inline' }}>
                            {displayText}
                        </pre>
                        {isLong && (
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, marginLeft: 4 }}
                                onClick={() => {
                                    setResultModalContent(text);
                                    setShowResultModal(true);
                                }}
                            >查看更多</Button>
                        )}
                    </div>
                );
            }
        },
        {
            title: '创建/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >编辑</Button>
                    <Popconfirm
                        title="确定要删除该记录吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            typ: currentRecord.typ !== undefined && currentRecord.typ !== null ? Number(currentRecord.typ) : '',
        } : {
            su_uid: '',
            typ: '',
            // 新增时不显示状态字段，param 默认值如下
            param: '{"real_name":"","id_card":"","phone":""}',
            result: ''
        };
        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Slot label="用户UID" field="su_uid" rules={[{ required: true, message: '请选择用户' }]}>
                    <UserSelector
                        value={formApi?.getValue('su_uid')}
                        onChange={v => formApi?.setValue('su_uid', Array.isArray(v) ? v[0] : v)}
                        multiple={false}
                        placeholder="请选择用户"
                    />
                </Form.Slot>
                <Form.Select
                    field="typ"
                    label="信用类型"
                    rules={[{ required: true, message: '请选择类型' }]}
                >
                    {Array.isArray(typList) && typList.length > 0 && typList.map(item => (
                        <Select.Option key={item.uid} value={Number(item.uid)}>{item.name}</Select.Option>
                    ))}
                </Form.Select>
                {/* 仅编辑时显示状态字段 */}
                {isEdit && (
                    <Form.Select
                        field="status"
                        label="状态"
                        rules={[{ required: true, message: '请选择状态' }]}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Form.Select>
                )}
                <Form.TextArea
                    field="param"
                    label="查询参数"
                    rows={3}
                />
                {/* 仅编辑时显示查询结果字段 */}
                {isEdit && (
                    <Form.TextArea
                        field="result"
                        label="查询结果"
                        rows={3}
                    />
                )}
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.userVerifyContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索用户UID/参数/结果"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="信用类型"
                        value={searchTyp}
                        onChange={setSearchTyp}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部类型</Select.Option>
                        {typList.map(item => (
                            <Select.Option key={item.uid} value={item.uid}>{item.name}</Select.Option>
                        ))}
                    </Select>
                    <Select
                        placeholder="状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 120 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >搜索</Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增查询</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(2, selectedRowKeys)}>批量失败</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量通过</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>
            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />
            <Modal
                title="新增用户查询"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {renderForm('add')}
            </Modal>
            <Modal
                title="编辑用户认证记录"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {renderForm('edit')}
            </Modal>
            <Modal
                title="完整结果"
                visible={showResultModal}
                onCancel={() => setShowResultModal(false)}
                onOk={() => setShowResultModal(false)}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{resultModalContent}</pre>
            </Modal>
        </div>
    );
};

export default UserVerify; 